import { Component, EventEmitter } from '@angular/core';
import { ActivatedRoute, UrlSegment } from '@angular/router';
import { AccessControlType } from '../../../foundation/models/enums/access-control-type.enum';
import { CorrespondenceIdentificationType } from '../../../foundation/models/enums/correspondence-identification-type.enum';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { DocumentItemKeyType } from '../../../shared/enums/document-item-key-type.enum';
import { scrollElementBelowHeader } from '../../../shared/helpers/scroll-helpers.model';
import { Dictionary } from '../../../shared/models/dictionary.model';
import { DocumentSearchCriteria } from '../../../shared/models/document/document-search-criteria.model';
import { MvdModalService } from '../../../shared/services/modal/mvd-modal.service';
import { CustomerAccountService } from '../../account/service/customer-account.service';
import { CustomerIdentification } from '../../models/customer-identification.model';
import { CustomerOrganizationName } from '../../models/customer-organization-name.model';
import { CustomerDetailTab } from '../../models/enums/customer-detail-tab.enum';
import { CustomerNameType } from '../../models/enums/customer-name-type.enum';
import { CustomerType } from '../../models/enums/customer-type.enum';
import { OrganizationDetailViewModel } from '../../models/views/organization-detail-view.model';
import { OrganizationDetailService } from '../organization-detail.service';
import { NoteModalComponent } from '../../../note/note-modal/note-modal.component';
import { CustomerDetailService } from '../customer-detail.service';
import { CustomerStatusType } from '../../../Foundation/models/enums/customer-status-type.enum';
import { AppSettings } from '../../../shared/settings/app-settings.model';
import { RedactionService } from '../../../shared/services/redaction/redaction.service';
import { SecurityTransactionType } from '../../../auth/models/security-transaction-type.enum';
import { SendCustomerSurveyModalComponent } from '../../contacts/send-customer-survey-modal.component';

@Component({
    selector: 'organization-customer',
    templateUrl: './organization-customer.html',
    inputs: ['customerNumber', 'inModal'],
    outputs: ['onBackClicked']
})

export class OrganizationCustomerComponent extends BaseComponent {
    public organizationDetails: OrganizationDetailViewModel;
    public customerNumber = 0;
    public inModal = false;
    public details: any[] = [];
    public names: any[] = [];
    public showAll = false;
    public showNotes = false;
    public title: string;
    public titleLink: string;
    public customerAccountNumberId: number;
    public showBack = false;
    public numberOfAccounts = 0;
    public searchDocumentDataCollection: DocumentSearchCriteria[] = [];
    public autoPopupNotesModal = false;
    public tab: CustomerDetailTab = CustomerDetailTab.Customer;
    public tabs: typeof CustomerDetailTab = CustomerDetailTab;
    public isFullRedactionNecessary = true;

    private fullNameList: any[];
    public customerTypes = new CustomerType();
    private customerNameTypes = new CustomerNameType();
    public accessControlTypes = new AccessControlType();
    public correspondenceIdentificationTypes = new CorrespondenceIdentificationType();
    public securityTransactionType = SecurityTransactionType;

    public ein: string;

    public vehicleDetailId: number;
    public customer: any[] = [];

    public customerStatusTypes: CustomerStatusType = new CustomerStatusType();

    onBackClicked: EventEmitter<boolean>;

    private CUSTOMER_IDENTIFICATION_TYPE_EIN = 6;

    constructor(public service: OrganizationDetailService,
        private modalService: MvdModalService,
        private _route: ActivatedRoute,
        private customerAccountService: CustomerAccountService,
        private customerDetailService: CustomerDetailService,
        private settings: AppSettings,
        private redactionService: RedactionService
    ) {
        super();
        this.onBackClicked = new EventEmitter<boolean>();
    }

    ngOnInit() {
        this.title = 'Customer Record Summary';
        this.titleLink = 'View Full Record';

        this.organizationDetails = new OrganizationDetailViewModel();
        this.organizationDetails.nameDetail = new CustomerOrganizationName();
        this.organizationDetails.accessControls = new Dictionary([]);

        let id;
        if (this.customerNumber != 0) {
            id = this.customerNumber;
            this.organizationDetails.id = id;
            this.showBack = true;
        } else {
            id = this._route.snapshot.params['id'];
            this.organizationDetails.id = id;
            this.customerNumber = id;
            const tab: string = this._route.snapshot.params['tab'];

            switch (tab) {
                case 'fleet':
                case 'vehicle':
                    this.tab = this.tabs.Vehicle;
                    break;
                case 'accounts':
                    this.tab = this.tabs.Accounts;
                    break;
                case 'affiliations':
                    this.tab = this.tabs.Affiliations;
                    break;
                case 'documents':
                    this.tab = this.tabs.Documents;
                    break;
                case 'correspondence':
                    this.tab = this.tabs.Correspondence;
                    break;
                case 'notes':
                    this.autoPopupNotesModal = true;
                    break;
                default:
                    break;
            }

            this.showBack = false;
        }

        this.customerAccountService.getNumberOfAccounts(this.customerNumber)
            .subscribe(
                result => this.numberOfAccounts = result.result,
                e => { }
            );

        if (id) {
            this.getDetails(id);
        }

        this.customer.push({ customerNumberId: this.customerNumber });

        const documentSearchCriteria1 = new DocumentSearchCriteria();
        documentSearchCriteria1.documentItemKeyTypeId = DocumentItemKeyType.Customer;
        documentSearchCriteria1.itemKeyId = this.customerNumber;
        this.searchDocumentDataCollection.push(documentSearchCriteria1);
    }

    acpChanged(isACP) {
        this.organizationDetails.isACP = isACP;

        this.getDetails(this.organizationDetails.id);
    }

    reloadData(yes) {
        this.getDetails(this.organizationDetails.id);
    }

    getDetails(customerNumber) {
        this.service.getData(customerNumber)
            .subscribe(result => {
                this.organizationDetails = result;

                this.initializeNotesModal();
            },
            e => this.handleValidationResult(e),
            () => {
                // Get Identifications
                for (var x = 0; x < this.organizationDetails.identifications.length; x++) {
                    var identification: CustomerIdentification;
                    identification = this.organizationDetails.identifications[x];

                    switch (identification.customerIdentificationTypeId) {
                        case this.CUSTOMER_IDENTIFICATION_TYPE_EIN:
                            this.ein = identification.customerIdentificationValue;
                            break;
                        default:
                            break;
                    }
                }

                this.details = [];
                this.details.push({ 'customerType': this.getValues(this.organizationDetails.customerTypeId, this.customerTypes), 'customerNumber': this.organizationDetails.id, 'ein': this.ein });

                this.fullNameList = this.organizationDetails.dbas.slice(0);

                this.names = [];
                this.names.push(this.organizationDetails.nameDetail);
                this.names = this.names.concat(this.organizationDetails.dbas);
                this.isFullRedactionNecessary = this.redactionService.isFullRedactionNecessary(this.organizationDetails.characteristics);
            });
    }

    getValues(id: number, types: any): string {
        if (id !== undefined && id !== null) {
            return types.FromId(id).displayValue;
        } else {
            return '';
        }
    }

    public changeTab(tab: CustomerDetailTab, id: number = null) {
        if (id) {
            if (tab == this.tabs.VehicleDetails) {
                this.vehicleDetailId = id;
            } else if (tab >= this.tabs.Accounts) {
                this.customerAccountNumberId = id;
            }
        }
        // this.showBack = tab != this.tabs.Customer;
        this.tab = tab;
    }

    navigateBack() {
        if (this.tab == this.tabs.AccountDetails) {
            this.tab = this.tabs.Accounts;
        } else if (this.tab == this.tabs.AccountEdit || this.tab == this.tabs.DisbursementRequest || this.tab == this.tabs.ServiceHistory) {
            this.tab = this.tabs.AccountDetails;
        } else if (this.tab == this.tabs.VehicleDetails) {
            this.tab = this.tabs.Vehicle;
        } else if (this.tab == this.tabs.Customer) {
            this.onBackClicked.emit(true);
        } else {
            this.tab = this.tabs.Customer;
        }
    }

    goTo(location: string) {
        const element = document.getElementById(location);
        scrollElementBelowHeader(element);
    }

    toggleView() {
        if (this.showAll) {
            this.title = 'Customer Record Summary';
            this.titleLink = 'View Full Record';
            this.showAll = false;
        } else {
            this.title = 'Full Customer Record';
            this.titleLink = 'View Summary';
            this.showAll = true;
        }
    }

    openNotesModal() {
        this.modalService.open(NoteModalComponent, this.organizationDetails.id);
    }

    initializeNotesModal() {
        if (this.autoPopupNotesModal) {
            this.autoPopupNotesModal = false;
            this.openNotesModal();
        }
    }

    openSendCustomerSurveyModal(): void {
        this.modalService.open(SendCustomerSurveyModalComponent, this.organizationDetails.id);
    }

    public customerAddressesUpdated = (addresses: any[]): void => {
        this.organizationDetails.addresses = addresses;
    };

    public customerStatusTypeChanged = (event): void => {
        this.organizationDetails.statusTypeId = event;
    };

    public async createAppointment() {
        let cNumber = this.customerNumber.toString();
        let orgName = this.organizationDetails.nameDetail.organizationName;
        await this.customerDetailService.checkForOrgCustomerAndCreateAppointment(cNumber, orgName)
            .subscribe(
                result => {
                    if (result.result == true) {
                        this.goToAppointmentCreation(cNumber);
                    }
                }
            );
    }

    public goToAppointmentCreation(customerNumberId: string) {
        const url = `${this.settings.asqAppointmentsUrl}?customerExternalId=${customerNumberId}&locationExternalId=${this.auth.businessUnitId}`;
        window.open(url, 'ASQ');
    }
}
